'use client'

import { useDashboardData } from '@/hooks/useDashboardData'
import { useFilterStore } from '@/stores/filterStore'
import { useBusinessUnitStore } from '@/stores/businessUnitStore'
import { useFilteredData } from '@/hooks/useFilters'
import { formatRelativeTime } from '@/lib/dates'
import { Clock } from 'lucide-react'
import type { FilterCategory } from '@/types'

const CATEGORIES: { value: FilterCategory; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'MSO', label: 'MSO' },
  { value: 'PSO', label: 'PSO' },
  { value: 'Internal', label: 'Internal' },
]

const PERIODS = [7, 30, 90, 180]

export default function Header() {
  const { activeEngineers, updatedAt, isLoading, engineerRoles, businessUnits, currentBuId } = useDashboardData()
  const setSelectedBuId = useBusinessUnitStore(s => s.setSelectedBuId)
  const { category, engineer, period, customDateFrom, customDateTo, setCategory, setEngineer, setPeriod, setCustomDateRange, clearCustomDateRange } = useFilterStore()

  // Date range state for picker
  const dateFrom = customDateFrom ? new Date(customDateFrom) : null
  const dateTo = customDateTo ? new Date(customDateTo) : null

  if (isLoading) return <div className="h-14 border-b border-slate-200 dark:border-slate-800" />

  const isIndividual = engineer !== 'all'

  return (
    <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-sm border-b border-slate-200 dark:bg-slate-900/80 dark:border-slate-800">
      <div className="flex items-center gap-3 px-6 h-14">
        {/* Business Unit */}
        {businessUnits.length > 1 && (
          <>
            <select value={currentBuId ?? ''} onChange={e => setSelectedBuId(parseInt(e.target.value))}
              className="text-sm bg-transparent border border-blue-300 dark:border-blue-700 text-blue-700 dark:text-blue-300 px-2 py-1.5 font-medium focus:outline-none focus:border-blue-500">
              {businessUnits.map(bu => <option key={bu.id} value={bu.id}>{bu.name}</option>)}
            </select>
            <div className="w-px h-5 bg-slate-200 dark:bg-slate-700" />
          </>
        )}
        {businessUnits.length === 1 && (
          <>
            <span className="text-sm font-medium text-slate-600 dark:text-slate-300 px-2">{businessUnits[0].name}</span>
            <div className="w-px h-5 bg-slate-200 dark:bg-slate-700" />
          </>
        )}

        {/* Category */}
        <div className="flex items-center gap-0.5">
          {CATEGORIES.map(c => (
            <button key={c.value} onClick={() => setCategory(c.value)}
              className={`px-2.5 py-1 text-sm font-medium transition-colors ${category === c.value ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:hover:text-slate-200 dark:hover:bg-slate-800'}`}>
              {c.label}
            </button>
          ))}
        </div>
        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700" />

        {/* Engineer */}
        <select value={engineer} onChange={e => setEngineer(e.target.value)}
          className="text-sm bg-transparent border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 px-2 py-1.5 focus:outline-none focus:border-blue-500">
          <option value="all">All Engineers ({activeEngineers.length})</option>
          {activeEngineers.map(e => <option key={e} value={e}>{e}</option>)}
        </select>

        {isIndividual && (
          <span className="text-xs text-slate-400 uppercase">{engineerRoles[engineer] || 'L1'}</span>
        )}

        <div className="w-px h-5 bg-slate-200 dark:bg-slate-700" />

        {/* Period */}
        <div className="flex items-center gap-0.5">
          {PERIODS.map(p => (
            <button key={p} onClick={() => { setPeriod(p); clearCustomDateRange() }}
              className={`px-2 py-1 text-sm font-medium transition-colors ${period === p && !customDateFrom ? 'bg-blue-600 text-white' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:hover:text-slate-200 dark:hover:bg-slate-800'}`}>
              {p}d
            </button>
          ))}
        </div>

        {/* Date range */}
        <div className="flex items-center gap-1.5">
          <input type="date" value={customDateFrom || ''} max={new Date().toISOString().slice(0,10)}
            onChange={e => {
              const from = e.target.value
              if (!from) return
              const to = customDateTo || new Date().toISOString().slice(0,10)
              setCustomDateRange(from, to)
            }}
            className="w-[130px] px-2 py-1 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 focus:outline-none focus:border-blue-500" />
          <span className="text-sm text-slate-400">to</span>
          <input type="date" value={customDateTo || ''} max={new Date().toISOString().slice(0,10)}
            onChange={e => {
              const to = e.target.value
              if (!to) return
              const from = customDateFrom || '2026-01-01'
              setCustomDateRange(from, to)
            }}
            className="w-[130px] px-2 py-1 text-sm bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 focus:outline-none focus:border-blue-500" />
          {customDateFrom && (
            <button onClick={clearCustomDateRange} className="text-sm text-slate-400 hover:text-red-500 px-1">&times;</button>
          )}
        </div>

        <div className="flex-1" />

        {/* Sync time */}
        {updatedAt && (
          <div className="flex items-center gap-1.5 text-xs text-slate-400">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            <Clock size={11} />
            <span>Last synced {formatRelativeTime(updatedAt)}</span>
          </div>
        )}
      </div>
    </header>
  )
}
